// Application lifecycle for the launcher.
// - Buffers `init` until the panel posts `ready`.
// - Bridges open/close/destroy to DOM + panelBridge.
// - Reflects panel events (request-fullscreen / request-close / resize).
// - FEATURE-34: resolves + propagates the active theme.

import type { InitOptions, ThemeName, WidgetMode } from '@/domain';
import { resolveClientCode, readMetaClientCode } from '@/infrastructure/clientCode';
import {
  applyMode,
  applyResponsiveLayout,
  applyTheme as applyButtonTheme,
  destroyWidget,
  injectWidget,
  isMobileViewport,
  setVisible,
  type DomHandles,
} from '@/infrastructure/domInjector';
import { mergeInit, readMetaContext } from '@/infrastructure/metaTag';
import { createPanelBridge } from '@/infrastructure/panelBridge';
import { config } from '@/infrastructure/config';
import { resolveTheme } from '@/theme/applyTheme';
import { normalizeApiBase } from '@/utils/normalize-api-base';
import { THEME_NAMES } from '@ba/protocol';

interface LifecycleState {
  handles: DomHandles | null;
  bridge: ReturnType<typeof createPanelBridge> | null;
  init: InitOptions | null;
  /** FEATURE-34 — resolved theme; null until `init` runs. */
  theme: ThemeName;
  ready: boolean;
  pendingOpen: WidgetMode | null;
  mode: WidgetMode;
  unsubscribePanel: (() => void) | null;
  /** FEATURE-35 — window resize listener disposer (reflows popup). */
  unsubscribeResize: (() => void) | null;
}

export interface Lifecycle {
  init(opts: InitOptions): void;
  open(mode?: WidgetMode): void;
  close(): void;
  destroy(): void;
  updateUser(user: NonNullable<InitOptions['user']>): void;
  updateContext(siteContext: NonNullable<InitOptions['siteContext']>): void;
  /** FEATURE-34 — switch theme at runtime. */
  updateTheme(theme: ThemeName): void;
  /** FEATURE-34 — list known themes (compile-time const). */
  themes(): readonly ThemeName[];
  /** Test helper: returns current state snapshot (read-only). */
  __debug(): Readonly<LifecycleState>;
}

export function createLifecycle(): Lifecycle {
  const state: LifecycleState = {
    handles: null,
    bridge: null,
    init: null,
    theme: 'default',
    ready: false,
    pendingOpen: null,
    mode: 'popup',
    unsubscribePanel: null,
    unsubscribeResize: null,
  };

  function ensureMounted(): void {
    if (state.handles !== null) return;
    const cfg = config();
    state.handles = injectWidget({
      panelUrl: cfg.panelUrl,
      theme: state.theme,
      // FEATURE-88 — forward the resolved client code on the iframe URL so a
      // hard-loaded panel reads it synchronously (no postMessage round-trip
      // needed for the very first request). Undefined drops the query field.
      clientCode: state.init?.clientCode,
      // FEATURE-120 — picks the language of the brand-neutral iframe title +
      // launcher button label.
      locale: state.init?.locale,
      // Dock corner (default bottom-left). Hosts with a left sidebar pass
      // 'bottom-right' so the launcher clears their nav rail.
      position: state.init?.position ?? 'bottom-left',
      // Host-customization options — domInjector applies defaults for any unset.
      margin: state.init?.margin,
      width: state.init?.width,
      height: state.init?.height,
      resizable: state.init?.resizable,
      icon: state.init?.icon,
      launcherSize: state.init?.launcherSize,
      hideLauncher: state.init?.hideLauncher,
      accentColor: state.init?.accentColor,
      onLauncherClick: () => open('popup'),
    });
    // FEATURE-35 — keep popup dimensions in sync with viewport changes.
    const handleResize = (): void => {
      if (state.handles === null) return;
      if (state.mode === 'popup') applyResponsiveLayout(state.handles);
    };
    window.addEventListener('resize', handleResize);
    state.unsubscribeResize = () => window.removeEventListener('resize', handleResize);
    state.bridge = createPanelBridge({ iframe: state.handles.iframe });
    state.unsubscribePanel = state.bridge.onPanelMessage((env) => {
      switch (env.type) {
        case 'ready':
          state.ready = true;
          flushInit();
          if (state.pendingOpen !== null) {
            open(state.pendingOpen);
            state.pendingOpen = null;
          }
          break;
        case 'request-fullscreen':
          state.mode = 'fullscreen';
          if (state.handles !== null) applyMode(state.handles, 'fullscreen');
          break;
        case 'exit-fullscreen':
          state.mode = 'popup';
          if (state.handles !== null) applyMode(state.handles, 'popup');
          break;
        case 'request-close':
          close();
          break;
        case 'resize':
          // Out of scope for FEATURE-01 — panel chooses its own height.
          break;
        case 'analytics-event':
          // No host analytics integration yet — silently drop.
          break;
      }
    });
  }

  function flushInit(): void {
    if (state.init === null || state.bridge === null || !state.ready) return;
    state.bridge.sendInit({
      apiBase: state.init.apiBase,
      siteContext: state.init.siteContext,
      user: state.init.user,
      // FEATURE-66 — forward what the host actually set; do NOT default
      // to 'vi'. Omission and explicit 'auto' both produce BE auto-detect
      // (PROTOCOL.md §1 + FEATURE-56). The earlier hard default made the
      // auto-detect path dormant for every host.
      locale: state.init.locale,
      theme: state.theme,
      // FEATURE-88 — forward the tenant id over the postMessage init payload
      // for parity with the iframe URL query. Undefined drops the field.
      clientCode: state.init.clientCode,
      // Host accent color — panel overrides its brand vars. Mirrored on the
      // iframe URL `?accent=` for pre-mount application; this is the runtime
      // path + parity.
      accentColor: state.init.accentColor,
    });
  }

  function init(opts: InitOptions): void {
    const merged = mergeInit(opts, readMetaContext());
    if (merged === null) {
      // Should never happen — opts already includes apiBase. Fail loudly.
      throw new Error('[BAAssistant.init] apiBase is required');
    }
    // FEATURE-40 — canonicalize the host-supplied apiBase ONCE here so the
    // panel never has to think about trailing slashes or trailing `/api`
    // mount segments. `mergeInit` already rejected empty input; `normalizeApiBase`
    // re-validates after `trim()`.
    const normalizedApiBase = normalizeApiBase(merged.apiBase);
    // FEATURE-88 — collapse `init.clientCode` + the `<meta name="ba-assistant-client">`
    // baseline into one validated string (or undefined). Validation is host-facing:
    // a malformed clientCode is dropped + warned in dev rather than thrown so
    // the BE-side Phase 2 fallback (worldtrans tenant) can still serve. Phase 5
    // flips this to a hard error after the BE makes the field required.
    const clientCode = resolveClientCode(merged.clientCode, readMetaClientCode());
    state.init = { ...merged, apiBase: normalizedApiBase, clientCode };
    state.theme = resolveTheme(merged.theme);
    ensureMounted();
    flushInit();
  }

  function open(mode: WidgetMode = 'popup'): void {
    if (state.handles === null) ensureMounted();
    // FEATURE-35 — auto-elevate popup→fullscreen on mobile viewports so
    // the tiny popup never appears on phones.
    const effectiveMode: WidgetMode =
      mode === 'popup' && isMobileViewport() ? 'fullscreen' : mode;
    if (!state.ready) {
      state.pendingOpen = effectiveMode;
      if (state.handles !== null) {
        setVisible(state.handles, true);
        applyMode(state.handles, effectiveMode);
      }
      state.mode = effectiveMode;
      return;
    }
    if (state.handles !== null) {
      setVisible(state.handles, true);
      applyMode(state.handles, effectiveMode);
    }
    state.mode = effectiveMode;
    if (state.bridge !== null) state.bridge.sendOpen({ mode: effectiveMode });
  }

  function close(): void {
    if (state.handles !== null) {
      // Reset to popup mode BEFORE hiding so the launcher button — which
      // `applyMode('fullscreen')` sets to `display:none` so it doesn't peek
      // through the fullscreen overlay — is restored. Without this, closing
      // from fullscreen mode leaves the button hidden and the user has no
      // way to re-open the chat short of a page reload.
      applyMode(state.handles, 'popup');
      setVisible(state.handles, false);
    }
    if (state.bridge !== null && state.ready) state.bridge.sendClose();
    // Keep internal state in sync — the next open() defaults to popup
    // unless an explicit mode is passed.
    state.mode = 'popup';
  }

  function destroy(): void {
    if (state.unsubscribePanel !== null) state.unsubscribePanel();
    if (state.unsubscribeResize !== null) state.unsubscribeResize();
    destroyWidget(state.handles);
    state.handles = null;
    state.bridge = null;
    state.init = null;
    state.theme = 'default';
    state.ready = false;
    state.pendingOpen = null;
    state.unsubscribePanel = null;
    state.unsubscribeResize = null;
  }

  function updateUser(user: NonNullable<InitOptions['user']>): void {
    if (state.init !== null) state.init = { ...state.init, user };
    if (state.bridge !== null && state.ready) state.bridge.sendUpdateUser(user);
  }

  function updateContext(siteContext: NonNullable<InitOptions['siteContext']>): void {
    if (state.init !== null) state.init = { ...state.init, siteContext };
    if (state.bridge !== null && state.ready) state.bridge.sendUpdateContext(siteContext);
  }

  function updateTheme(theme: ThemeName): void {
    const resolved = resolveTheme(theme);
    if (resolved === state.theme) return;
    state.theme = resolved;
    if (state.init !== null) state.init = { ...state.init, theme: resolved };
    if (state.handles !== null) applyButtonTheme(state.handles, resolved);
    if (state.bridge !== null && state.ready) state.bridge.sendUpdateTheme(resolved);
  }

  function themes(): readonly ThemeName[] {
    return THEME_NAMES;
  }

  return {
    init,
    open,
    close,
    destroy,
    updateUser,
    updateContext,
    updateTheme,
    themes,
    __debug() {
      return state;
    },
  };
}
